import { SuccessIcon, SummaryRow, MerchantButton } from "@components";
import { formatAmount } from "@utils";
import { useOutletContext } from "react-router-dom";

const PRSuccess = () => {
  const {
    merchant,
    paymentType,
    amountPaid,
    reference,
    lastSuccessfulPayment,
    webhookPrResponse,
    methodStepData,
  } = useOutletContext();

  // Prefer methodStepData, but when page is refreshed this can be null. Fallback to last successful payment.
  const lastPayment = lastSuccessfulPayment;
  const fallbackFinalAmount =
    !methodStepData?.finalAmount &&
    lastPayment &&
    typeof lastPayment.corporateCardSurcharge === "number"
      ? {
          baseAmount: lastPayment.amount,
          totalAmount: lastPayment.amount + lastPayment.corporateCardSurcharge,
          totalFeesAmount: lastPayment.corporateCardSurcharge,
          totalFeesPercentage:
            lastPayment.amount > 0
              ? (lastPayment.corporateCardSurcharge / lastPayment.amount) * 100
              : 0,
        }
      : null;

  const finalAmount = methodStepData?.finalAmount || fallbackFinalAmount;
  const hadCorporateSurcharge = finalAmount && finalAmount.totalFeesAmount > 0;
  const paidWithKlarna =
    methodStepData?.paidWithKlarna || paymentType === "ThirdParty";

  let displayAmount, surchargeAmount, surchargePercentage;

  if (hadCorporateSurcharge) {
    displayAmount = finalAmount.baseAmount;
    surchargeAmount = finalAmount.totalFeesAmount;
    surchargePercentage = finalAmount.totalFeesPercentage;
  } else {
    displayAmount =
      finalAmount?.totalAmount ?? amountPaid ?? webhookPrResponse?.amount;
  }

  const summaryRows = [
    {
      key: "recipient",
      pad: true,
      label: "Recipient",
      value: merchant.name,
    },
    ...(displayAmount
      ? [
          {
            key: "amount",
            pad: true,
            label: "Amount",
            value: formatAmount(displayAmount),
          },
        ]
      : []),
    {
      key: "reference",
      pad: true,
      label: "Reference",
      value: reference,
    },
    ...(paidWithKlarna
      ? [
          {
            key: "klarna",
            pad: true,
            label: "Payment method",
            value: "Klarna",
          },
        ]
      : []),
    ...(hadCorporateSurcharge
      ? [
          {
            key: "corporate-method",
            pad: true,
            label: "Payment method",
            value: "Corporate card",
          },
          {
            key: "corporate-fee",
            pad: true,
            label: "Corporate card fee",
            value: formatAmount(surchargeAmount),
          },
        ]
      : []),
  ];

  return (
    <div className="bg-zinc-100 h-dvh grow lg:h-full">
      <div className="w-full  lg:w-[480px] mx-auto flex flex-col items-center">
        <div className="size-[160px] mx-auto flex items-center justify-center mt-10">
          <SuccessIcon merchant={merchant} />
        </div>
        <h1 className="font-semibold text-xl mb-2">Payment successful</h1>
        <p className="text-zinc-500 text-center mb-5">
          We've successfully processed your payment
        </p>
        <div className="w-full px-8 py-5">
          <div className="bg-white rounded-xl border border-zinc-300 w-full max-w-lg mx-auto">
            {summaryRows.map(({ key, pad, label, value }, index) => (
              <SummaryRow
                key={key}
                pad={pad}
                isLast={index === summaryRows.length - 1}
                label={label}
                value={value}
              />
            ))}
          </div>
        </div>
        {merchant.paymentCompleteRedirectUrl && (
          <div className="px-8 w-full flex justify-center">
            <MerchantButton
              className="mt-5 w-full max-w-[320px]"
              rounded="full"
              onClick={() =>
                (window.location.href = merchant.paymentCompleteRedirectUrl)
              }
              merchant={merchant}
            >
              Done
            </MerchantButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default PRSuccess;
